import { downloadFile } from '@/helpers';
import { Button, Checkbox, message, Space } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import ModalForm from '../ModalForm';

export interface ExportColumnConfig {
  dataIndex: string;
  title: string;
  order: number;
}

export interface ExportExcelModalProps {
  open: boolean;
  onCancel: () => void;
  exportColumns: ExportColumnConfig[];
  exportApi: (body: any) => Promise<any>;
  selectedIds?: (string | number)[];
  filename?: string;
  title?: string;
  params?: any;
}

const ExportExcelModal: React.FC<ExportExcelModalProps> = ({
  open,
  onCancel,
  exportColumns,
  exportApi,
  selectedIds = [],
  filename = 'export',
  title = 'Xuất Excel',
  params = {},
}) => {
  const [selectedColumns, setSelectedColumns] = useState<ExportColumnConfig[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedColumns([...exportColumns]);
    }
  }, [open, exportColumns]);

  const allSelected = useMemo(
    () => selectedColumns.length === exportColumns.length && exportColumns.length > 0,
    [selectedColumns.length, exportColumns.length]
  );

  const indeterminate = useMemo(
    () => selectedColumns.length > 0 && selectedColumns.length < exportColumns.length,
    [selectedColumns.length, exportColumns.length]
  );

  const handleSelectAll = (checked: boolean) => {
    setSelectedColumns(checked ? [...exportColumns] : []);
  };

  const handleColumnChange = (dataIndex: string, checked: boolean) => {
    setSelectedColumns(prev => {
      if (checked) {
        if (prev.some(col => col.dataIndex === dataIndex)) return prev;
        const columnToAdd = exportColumns.find(col => col.dataIndex === dataIndex);
        return columnToAdd ? [...prev, columnToAdd] : prev;
      }
      return prev.filter(col => col.dataIndex !== dataIndex);
    });
  };

  const extractBlobFromResponse = (response: any): Blob | null => {
    if (!response) return null;
    if (response instanceof Blob) return response;
    if (response.data instanceof Blob) return response.data;

    if (response.data) {
      return new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    }

    return null;
  };

  const buildFilename = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    return `${filename}_${timestamp}.xlsx`;
  };

  const handleExport = async () => {
    setLoading(true);
    if (selectedColumns.length === 0) {
      message.warning('Vui lòng chọn ít nhất một cột để xuất');
      return;
    }

    const body: any = {
      columns: selectedColumns,
      ...(selectedIds.length > 0 ? { exportIds: selectedIds } : {}),
      ...params,
    };

    message.loading({ content: 'Đang xuất file Excel...', key: 'export' });

    try {
      const response = await exportApi(body);

      const blob = extractBlobFromResponse(response);

      if (!blob) {
        message.error({ content: 'Không thể tải file Excel. Vui lòng thử lại.', key: 'export' });
        return;
      }

      downloadFile(blob, buildFilename());
      message.success({ content: 'Xuất Excel thành công', key: 'export' });
      onCancel();
    } catch (error: any) {
      console.error('Export error:', error);
      message.error({
        content: error?.message || 'Xuất Excel thất bại',
        key: 'export',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalForm
      title={title}
      open={open}
      onCancel={onCancel}
      width={600}
      footer={[
        <Button key='cancel' onClick={onCancel}>
          Hủy
        </Button>,
        <Button key='export' type='primary' onClick={handleExport} loading={loading}>
          Xuất Excel
        </Button>,
      ]}
    >
      <Space direction='vertical' style={{ width: '100%' }} size='middle'>
        <div>
          <Checkbox
            checked={allSelected}
            indeterminate={indeterminate}
            onChange={e => handleSelectAll(e.target.checked)}
          >
            <strong>Chọn tất cả</strong>
          </Checkbox>
        </div>

        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <Space direction='vertical' style={{ width: '100%' }}>
            {exportColumns.map(col => {
              const isChecked = selectedColumns.some(
                selected => selected.dataIndex === col.dataIndex
              );
              return (
                <Checkbox
                  key={col.dataIndex}
                  checked={isChecked}
                  onChange={e => handleColumnChange(col.dataIndex, e.target.checked)}
                >
                  {col.title}
                </Checkbox>
              );
            })}
          </Space>
        </div>

        {selectedIds.length > 0 ? (
          <div style={{ color: '#666', fontSize: 12 }}>
            Sẽ xuất {selectedIds.length} bản ghi đã chọn
          </div>
        ) : (
          <div style={{ color: '#666', fontSize: 12 }}>Sẽ xuất tất cả bản ghi</div>
        )}
      </Space>
    </ModalForm>
  );
};

export default ExportExcelModal;
